/**
 * headpose.js — lazy MediaPipe FaceLandmarker + head yaw/pitch heuristics.
 *
 * The heavy @mediapipe/tasks-vision package (and its ~11 MB wasm runtime) is
 * pulled in via a DYNAMIC import, so it lands in its own Vite chunk and never
 * bloats the Mini App bundle. Only the /capture browser page ever calls
 * createFaceTracker(), so the Telegram webview never downloads any of it.
 *
 * The wasm fileset and the .task model are SELF-HOSTED under /mediapipe/* (see
 * public/mediapipe/) — no CDN dependency, for flaky-mobile-network reliability.
 *
 * yaw/pitch here are ONLY used to decide WHEN to auto-snap the still. The real
 * liveness + anti-spoof verdict is the server sidecar's job (web_punch runs the
 * same /verify the native path uses). So a mis-tuned threshold degrades to the
 * ~5 s fallback capture, never to a false accept.
 */

// Self-hosted asset roots (absolute from origin — /capture is served at the
// domain root, davomat.oneplusone.uz/capture).
const WASM_ROOT = '/mediapipe/wasm';
const MODEL_URL = '/mediapipe/face_landmarker.task';

// Face-mesh landmark indices we sample (MediaPipe canonical 468-point mesh).
const NOSE_TIP = 1;
const CHEEK_L = 234; // image-left face edge
const CHEEK_R = 454; // image-right face edge
const FOREHEAD = 10;
const CHIN = 152;

/**
 * Lazily load the wasm runtime + model and build a VIDEO-mode FaceLandmarker.
 * Tries the GPU delegate first, falls back to CPU (older/locked-down mobiles).
 * Returns the FaceLandmarker instance (caller must .close() it when done).
 *
 * Pass { blendshapes:true } for the IN-APP live-capture screen, which needs the
 * ARKit-style faceBlendshapes (smile / eyeBlink*) to detect the action cue.
 * The browser /capture page leaves it false — it only needs yaw/pitch.
 */
export async function createFaceTracker({ blendshapes = false } = {}) {
  const vision = await import('@mediapipe/tasks-vision');
  const { FaceLandmarker, FilesetResolver } = vision;
  const fileset = await FilesetResolver.forVisionTasks(WASM_ROOT);

  const opts = (delegate) => ({
    baseOptions: { modelAssetPath: MODEL_URL, delegate },
    runningMode: 'VIDEO',
    numFaces: 1,
    outputFaceBlendshapes: blendshapes,
    outputFacialTransformationMatrixes: false,
  });

  try {
    return await FaceLandmarker.createFromOptions(fileset, opts('GPU'));
  } catch (_e) {
    return await FaceLandmarker.createFromOptions(fileset, opts('CPU'));
  }
}

/**
 * Lazily load MediaPipe's DrawingUtils + the face-mesh tesselation connector
 * set, for drawing the translucent mesh overlay in the in-app live capture.
 * Shares the same dynamic-import chunk as createFaceTracker (module cached), so
 * this adds no extra download.
 */
export async function loadDrawing() {
  const vision = await import('@mediapipe/tasks-vision');
  return {
    DrawingUtils: vision.DrawingUtils,
    FACE_LANDMARKS_TESSELATION: vision.FaceLandmarker.FACE_LANDMARKS_TESSELATION,
  };
}

/**
 * Normalized head yaw/pitch from a landmark array (each point {x,y} in [0,1]
 * image space). yaw≈0 / pitch≈0 when facing the camera head-on.
 *  yaw  > 0  → nose shifted toward image-right
 *  pitch> 0  → nose shifted toward image-bottom (chin-ward)
 * Returns null if the mesh is missing points.
 */
export function yawPitch(landmarks) {
  if (!landmarks || landmarks.length <= CHEEK_R) return null;
  const nose = landmarks[NOSE_TIP];
  const cl = landmarks[CHEEK_L];
  const cr = landmarks[CHEEK_R];
  const fh = landmarks[FOREHEAD];
  const ch = landmarks[CHIN];
  if (!nose || !cl || !cr || !fh || !ch) return null;

  const faceW = Math.abs(cr.x - cl.x) || 1e-6;
  const faceH = Math.abs(ch.y - fh.y) || 1e-6;
  const yaw = (nose.x - (cl.x + cr.x) / 2) / faceW;
  const pitch = (nose.y - (fh.y + ch.y) / 2) / faceH;
  return { yaw, pitch };
}

// Deviation (from the calibrated neutral baseline) needed to accept a cue.
// Generous enough to trigger on a clear head turn, tight enough to ignore idle
// jitter. Tune on-device if the auto-capture feels too eager / too reluctant.
export const YAW_THRESH = 0.11;
export const PITCH_THRESH = 0.07;

/**
 * Does a baseline-relative (dYaw,dPitch) satisfy the requested cue?
 *
 * NOTE ON SIGN: landmarks come from the RAW (un-mirrored) camera frame. When a
 * person turns their head to their OWN left, an observer (the camera) sees the
 * nose swing toward image-right → dYaw > 0. Hence turn_left ⇒ +yaw. If field
 * testing shows the axis inverted on a given device, flip these four signs —
 * the server cue check is the source of truth, this only gates auto-capture.
 */
export function matchesCue(cue, dYaw, dPitch) {
  switch (cue) {
    case 'turn_left': return dYaw > YAW_THRESH;
    case 'turn_right': return dYaw < -YAW_THRESH;
    case 'look_up': return dPitch < -PITCH_THRESH;
    case 'look_down': return dPitch > PITCH_THRESH;
    default: return false;
  }
}

/* ==========================================================================
   IN-APP LIVE CAPTURE — face placement + blendshape action detection.
   Used only by src/screens/InAppLiveCapture.jsx (the Mini-App-webview path).
   ========================================================================== */

// The guide oval, expressed in NORMALIZED frame coords ([0,1], same space as the
// MediaPipe landmarks). cx/cy = center, rx/ry = radii. The overlay canvas draws
// this exact ellipse and the placement check compares the face box against it in
// the SAME space, so the ring the worker sees matches the accept condition.
export const OVAL = { cx: 0.5, cy: 0.5, rx: 0.33, ry: 0.42 };

// Placement thresholds. Face width is measured as a FRACTION OF THE OVAL WIDTH
// (2*rx). Tune on-device if "come closer" / "move back" feels off.
export const FACE_W_MIN = 0.45; // below → too far (face too small) → "come closer"
export const FACE_W_MAX = 0.75; // above → too near (face too big)  → "move back"
export const CENTER_TOL_X = 0.5; // allowed |cx-oval.cx| as a fraction of rx
export const CENTER_TOL_Y = 0.5; // allowed |cy-oval.cy| as a fraction of ry

/**
 * Where is the face relative to the guide oval?
 * Returns { placed, reason, cx, cy, wr } where reason ∈
 *   'none'      — no face (or the mesh is empty)
 *   'small'     — face too far (width < FACE_W_MIN of oval width)  → come closer
 *   'big'       — face too near (width > FACE_W_MAX of oval width) → move back
 *   'offcenter' — face box center outside the centering tolerance
 *   'ok'        — properly placed (ring goes GREEN)
 * `wr` is the face-width / oval-width ratio (for debugging / hysteresis).
 */
export function facePlacement(landmarks) {
  if (!landmarks || !landmarks.length) return { placed: false, reason: 'none' };
  let minX = 1, minY = 1, maxX = 0, maxY = 0;
  for (const p of landmarks) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }
  const fw = maxX - minX;
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  const wr = fw / (OVAL.rx * 2);
  if (wr < FACE_W_MIN) return { placed: false, reason: 'small', cx, cy, wr };
  if (wr > FACE_W_MAX) return { placed: false, reason: 'big', cx, cy, wr };
  if (Math.abs(cx - OVAL.cx) > OVAL.rx * CENTER_TOL_X ||
      Math.abs(cy - OVAL.cy) > OVAL.ry * CENTER_TOL_Y) {
    return { placed: false, reason: 'offcenter', cx, cy, wr };
  }
  return { placed: true, reason: 'ok', cx, cy, wr };
}

// ---- action (liveness) detection via faceBlendshapes -------------------------

/** Read one blendshape score ([0,1]) by ARKit category name; 0 if absent. */
export function blend(shapes, name) {
  if (!shapes || !shapes.length) return 0;
  const c = shapes.find((s) => s.categoryName === name);
  return c ? c.score : 0;
}

// Blendshape thresholds for the three action cues. Tune on-device.
export const SMILE_THRESH = 0.5;      // (mouthSmileLeft+mouthSmileRight)/2 over this
export const EYE_CLOSED_THRESH = 0.5; // the winking eye must be at least this shut
export const EYE_OPEN_MAX = 0.3;      // the OTHER eye must be at most this shut

/**
 * ⚠ MIRROR / SIDE-MAPPING FLIP — the one knob to turn after the first on-device test.
 *
 * The front-camera preview is MIRRORED on screen, but MediaPipe blendshape names
 * ('eyeBlinkLeft'/'eyeBlinkRight') follow the SUBJECT'S OWN side (ARKit
 * convention). So when we tell the worker "close your LEFT eye", the blendshape
 * that actually fires can be eyeBlinkLeft OR eyeBlinkRight depending on how those
 * two conventions line up on the real device. If a device test shows the wink
 * side inverted (closing the correct eye is rejected), flip EYE_SIDE_FLIP to
 * true — and change NOTHING else. Smile is side-agnostic and always safe.
 */
export const EYE_SIDE_FLIP = false;

/** Map the eye we ASKED for (in the worker's terms) → {close,open} blendshapes. */
function blinkNamesFor(side) {
  const useLeft = (side === 'left') !== EYE_SIDE_FLIP; // XOR the flip
  return useLeft
    ? { close: 'eyeBlinkLeft', open: 'eyeBlinkRight' }
    : { close: 'eyeBlinkRight', open: 'eyeBlinkLeft' };
}

/**
 * Does the current faceBlendshapes set satisfy the requested GESTURE action?
 *   'smile'      — average mouth-smile score over SMILE_THRESH (side-agnostic)
 *   'wink_left'  — worker's LEFT eye shut AND right eye open (asymmetric)
 *   'wink_right' — mirror of wink_left
 * The eye mapping goes through blinkNamesFor → EYE_SIDE_FLIP (see above).
 * NOTE: 'still' is NOT a blendshape gesture — it is the ABSENCE of a gesture +
 * landmark stability, so it is checked in the capture screen, not here.
 */
export function matchesAction(action, shapes) {
  if (!shapes || !shapes.length) return false;
  if (action === 'smile') {
    const s = (blend(shapes, 'mouthSmileLeft') + blend(shapes, 'mouthSmileRight')) / 2;
    return s > SMILE_THRESH;
  }
  const side = action === 'wink_left' ? 'left'
    : action === 'wink_right' ? 'right' : null;
  if (!side) return false;
  const m = blinkNamesFor(side);
  return blend(shapes, m.close) > EYE_CLOSED_THRESH &&
         blend(shapes, m.open) < EYE_OPEN_MAX;
}

/**
 * The RAW [0,1] intensity of the requested GESTURE action's driving blendshape
 * — smile → averaged mouth-smile, wink → the closing-eye score. Used by the
 * in-app liveness gate to detect a LOW→HIGH TRANSITION (a live movement) rather
 * than a static high value (a held-up photo of a smiling face). Returns 0 for
 * 'still' / unknown actions (their liveness comes from micro-motion instead).
 */
export function actionSignal(action, shapes) {
  if (!shapes || !shapes.length) return 0;
  if (action === 'smile') {
    return (blend(shapes, 'mouthSmileLeft') + blend(shapes, 'mouthSmileRight')) / 2;
  }
  const side = action === 'wink_left' ? 'left'
    : action === 'wink_right' ? 'right' : null;
  if (!side) return 0;
  return blend(shapes, blinkNamesFor(side).close);
}

// A gesture's driving signal must first DIP below this ("relaxed" baseline)
// before rising over the match threshold for the movement to count as a live
// transition. A static photo that starts already-smiling never dips → rejected.
export const LIVENESS_LOW = 0.15;

// "Neutral face" gates for the 'still' action — the face must show no strong
// smile and both eyes open, so 'still' means genuinely holding a resting face.
export const NEUTRAL_SMILE_MAX = 0.35;
export const NEUTRAL_EYE_MAX = 0.45;

/** True when NO strong gesture is active (resting face) — used by 'still'. */
export function isNeutral(shapes) {
  if (!shapes || !shapes.length) return true; // no blendshapes → rely on landmarks
  const smile = (blend(shapes, 'mouthSmileLeft') + blend(shapes, 'mouthSmileRight')) / 2;
  const blinkL = blend(shapes, 'eyeBlinkLeft');
  const blinkR = blend(shapes, 'eyeBlinkRight');
  return smile < NEUTRAL_SMILE_MAX && blinkL < NEUTRAL_EYE_MAX && blinkR < NEUTRAL_EYE_MAX;
}

/** Nose-tip {x,y} (normalized) — the anchor the 'still' stability test tracks. */
export function noseXY(landmarks) {
  if (!landmarks || landmarks.length <= NOSE_TIP) return null;
  const n = landmarks[NOSE_TIP];
  return n ? { x: n.x, y: n.y } : null;
}

// 'still' stability band (normalized frame units). The nose must stay within
// STILL_MAX_DEV of its running mean across the hold window; a per-frame jitter
// above STILL_MICRO_MIN is treated as live micro-motion (a frozen photo has
// none → liveness_ok=false, though the server anti-spoof is the real verdict).
export const STILL_MAX_DEV = 0.02;
export const STILL_MICRO_MIN = 0.0012;

/**
 * The in-app action cues; pick one at random once the face is placed. 'still'
 * (be-still) is included as a low-effort option and as an on-device liveness
 * probe (natural micro-motion vs a frozen photo).
 */
export const LIVE_ACTIONS = ['smile', 'wink_left', 'wink_right', 'still'];

export function randomAction() {
  return LIVE_ACTIONS[Math.floor(Math.random() * LIVE_ACTIONS.length)];
}
